import {
  AppBar,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  MenuItem,
  Slide,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import { MuiFileInput } from "mui-file-input";
import { GridColDef, DataGrid } from "@mui/x-data-grid";
import { executeAlert } from "../../../../../../application/states/toast-alert";
import { format } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ptBR from "date-fns/locale/pt-BR";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  darkenColor,
  dataGridTexts,
  formatCep,
  formatCnpj,
  formatTelefoneBrazil,
  onlyNumbers,
} from "../../../../../../utils";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../application/states/hooks";
import { useBackofficeService } from "../../../../../../use-cases/backoffice";
import { useUserService } from "../../../../../../use-cases/user";
import { theme } from "../../../../../styles";
import { WhiteLabelCompanyForm } from "./WhiteLabelCompanyForm";
import { resetSelectedSellerState } from "../../../../../../application/states/user/user-slice";
import { replace } from "lodash";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const CompanyForm = () => {
  const handleChange = (event: any) => {
    const inputValor = event.target.value;

    // Remover todos os caracteres que não sejam números, ponto decimal ou vírgula
    const valorSemSimbolos = inputValor.replace(/[^0-9.,]/g, "");

    // Verificar se há mais de um ponto decimal ou vírgula
    const matchPontos = valorSemSimbolos.match(/[.,]/g);
    if (matchPontos && matchPontos.length > 1) {
      return; // Valor inválido, não atualizar o estado
    }

    // Substituir a vírgula por ponto para garantir um formato numérico correto
    const valorFormatado = valorSemSimbolos.replace(",", ".");

    setPrice(parseFloat(valorFormatado));
  };

  const handleSeparatedCreditsPriceChange = (event: any) => {
    const inputValor = event.target.value;

    // Remover todos os caracteres que não sejam números, ponto decimal ou vírgula
    const valorSemSimbolos = inputValor.replace(/[^0-9.,]/g, "");

    // Verificar se há mais de um ponto decimal ou vírgula
    const matchPontos = valorSemSimbolos.match(/[.,]/g);
    if (matchPontos && matchPontos.length > 1) {
      return; // Valor inválido, não atualizar o estado
    }

    // Substituir a vírgula por ponto para garantir um formato numérico correto
    const valorFormatado = valorSemSimbolos.replace(",", ".");

    setSeparatedCreditsPrice(parseFloat(valorFormatado));
  };

  // Função para formatar o valor monetário exibido
  const formatarValor = (valor: any) => {
    if (valor) {
      if (valor.split) {
        const partes = valor.split(/[.,]/); // Divide o valor em partes separadas por '.' ou ','
        const inteiro = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, "."); // Formata a parte inteira com separador de milhar
        const decimal = partes[1] ? partes[1].padEnd(2, "0") : "00"; // Completa até duas casas decimais
        return `${inteiro},${decimal}`;
      }
    }
  };

  //const selectedCompany = useAppSelector((state) => state.backofficeCompany.selectedCompany)
  const { resaleConfig } = useAppSelector(
    (state) => state.backofficeCompany.selectedCompanyResaleConfig,
  );
  const userState = useAppSelector((state) => state.user);
  const isReadOnly =
    userState.userType === "SELLER" ||
    userState.userType === "SALES SUPERVISOR";
  const open = useAppSelector(
    (state) => state.backofficeCompany.companyForm.open,
  );
  const companyFormFields = useAppSelector(
    (state) => state.backofficeCompany.companyForm.formFields,
  );
  //const selectedSeller = useAppSelector((state) => state.user.selectedSeller)
  const file = useAppSelector(
    (state) => state.backofficeCompany.companyForm.imageFile,
  );
  const company = useAppSelector((state) => state.company.company);
  const subscription = useAppSelector((state) => state.subscription);
  const dispatch = useAppDispatch();
  const [min, setMin] = useState(0);
  const rawSelectedCompany = useAppSelector(
    (state) => state.backofficeCompany.selectedCompany,
  );
  // Empresas antigas podem vir sem assinatura/faixas/créditos avulsos: garante arrays para não quebrar a tela
  const selectedCompany = useMemo(
    () =>
      rawSelectedCompany && {
        ...rawSelectedCompany,
        subscription: {
          ...rawSelectedCompany.subscription,
          ranges: rawSelectedCompany.subscription?.ranges ?? [],
        },
        separatedCredits: rawSelectedCompany.separatedCredits ?? [],
      },
    [rawSelectedCompany],
  );
  const selectedSeller = useAppSelector((state) => state.user.selectedSeller);
  useEffect(() => {
    if (selectedCompany?.subscription.ranges.length === 0) {
      setMin(0);
    } else {
      let maxPrev =
        selectedCompany?.subscription.ranges[
          selectedCompany?.subscription.ranges.length - 1
        ]?.max;
      if (maxPrev) {
        setMin(maxPrev + 1);
      }
      // Regras específicas por tipo de plano
      const plano = selectedCompany?.subscription.name;
      if (plano === "FEE MENSAL") {
        if (selectedCompany?.subscription.ranges.length === 0) {
          setFixedPrice(true); // Primeiro range = fixo
        } else {
          setFixedPrice(false); // Segundo range em diante = por registro
        }
      } else if (plano === "PÓS PAGO") {
        setFixedPrice(false);
      } else if (plano === "PRÉ PAGO") {
        setFixedPrice(true);
      } else {
        setFixedPrice(false);
      }
    }
  }, [
    selectedCompany?.subscription.ranges,
    selectedCompany?.subscription.name,
  ]);

  const [max, setMax] = useState<number | undefined>(undefined);
  const [displayValue, setDisplayValue] = useState<string>("");

  const [price, setPrice] = useState<number | undefined>(undefined);

  const [separatedCreditsValue, setSeparatedCreditsValue] = useState<
    number | undefined
  >(undefined);
  const [separatedCreditsPrice, setSeparatedCreditsPrice] = useState<
    number | undefined
  >(undefined);
  const [expDate, setExpDate] = useState<Date | null>(null);
  const { selectSeller } = useUserService();
  const [maxLimitError, setMaxLimitError] = useState<boolean>(false);

  const {
    selectCompany,
    feedCompanyForm,
    closeCompanyForm,
    feedAddressFields,
    createCompany,
    updateCompany,
    setCompanyFileImage,
    addSeparatedCredits,
    resetSelectedCompanyResaleConfig,
    validateLimitMaxRange,
  } = useBackofficeService();

  const separatedCreditsColumns: GridColDef[] = [
    {
      field: "total",
      headerName: "Total",
      headerClassName: "headerStyles",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        return params.value?.toLocaleString("pt-BR");
      },
    },
    {
      field: "price",
      headerName: "Preço",
      headerClassName: "headerStyles",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        return `R$ ${params.value?.toLocaleString("pt-BR", {
          minimumFractionDigits: params.value < 1 ? 4 : 2,
        })}`;
      },
    },
    {
      field: "createdAt",
      headerName: "Data de criação",
      headerClassName: "headerStyles",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        return format(new Date(params.value), "dd/MM/yyyy");
      },
    },
    {
      field: "expDate",
      headerName: "Data de expiração",
      headerClassName: "headerStyles",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        return format(new Date(params.value), "dd/MM/yyyy");
      },
    },
  ];

  const rangeColumns: GridColDef[] = [
    {
      field: "delete",
      headerName: "Excluir",
      headerClassName: "headerStyles",
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        /* Only render this button if the row is the last one */
        if (params.value === true) {
          return (
            <Button
              disabled={userState.userType === "SELLER"}
              endIcon={<DeleteIcon />}
              onClick={() => {
                if (selectedCompany) {
                  selectCompany({
                    company: {
                      ...selectedCompany.company,
                    },
                    subscription: {
                      ...selectedCompany.subscription,
                      ranges: selectedCompany.subscription.ranges.filter(
                        (range, index) => {
                          return index !== params.row.id;
                        },
                      ),
                    },
                    separatedCredits: selectedCompany.separatedCredits,
                    resaleConfig: resaleConfig,
                  });
                }
              }}
            >
              Excluir
            </Button>
          );
        } else {
          return null;
        }
      },
    },
    {
      field: "min",
      headerName: "Mínimo",
      headerClassName: "headerStyles",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        if (params.value === 0) {
          return "MIN - (0)";
        }
        return params.value?.toLocaleString("pt-BR");
      },
    },
    {
      field: "max",
      headerName: "Máximo",
      headerClassName: "headerStyles",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        return params.value?.toLocaleString("pt-BR") ?? "MAX - (999.999.999)";
      },
    },
    {
      field: "fixedPrice",
      headerName: "Preço fixo (FEE)",
      headerClassName: "headerStyles",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        if (params.value) {
          return `R$ ${params.value?.toLocaleString("pt-BR", {
            minimumFractionDigits: params.value < 1 ? 4 : 2,
          })}`;
        } else {
          return "";
        }
      },
    },
    {
      field: "pricePerRegister",
      headerName: "Preço por registro",
      headerClassName: "headerStyles",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell(params) {
        if (selectedCompany?.subscription.name === "POC") {
          return "R$ 0,00";
        }
        if (params.value) {
          return `R$ ${params.value?.toLocaleString("pt-BR", {
            minimumFractionDigits: params.value < 1 ? 4 : 2,
          })}`;
        } else {
          return "";
        }
      },
    },
  ];

  const [isValidating, setIsValidating] = useState<boolean>(false);
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCursorPos = useRef<number>(0);

  // Função específica para o campo Máximo
  const handleMaxChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    // Salva a posição atual do cursor para restaurar depois
    lastCursorPos.current = input.selectionStart || 0;

    const inputValue = input.value;

    // Formata o valor mantendo apenas números
    const onlyNumbers = inputValue.replace(/\D/g, "");
    let formattedValue = "";

    if (onlyNumbers) {
      const number = parseInt(onlyNumbers, 10);
      if (!isNaN(number)) {
        formattedValue = number.toLocaleString("pt-BR");
      }
    }

    // Atualiza o valor de exibição
    setDisplayValue(formattedValue);

    // Converte para valor numérico para o backend
    const numericValue = onlyNumbers ? Number(onlyNumbers) : undefined;
    setMax(numericValue);

    // Limpa timeout anterior de validação
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Validação em tempo real com debounce (apenas para POC)
    if (
      numericValue &&
      numericValue > 0 &&
      selectedCompany?.subscription.name === "POC"
    ) {
      setMaxLimitError(false);

      validationTimeoutRef.current = setTimeout(async () => {
        try {
          const result = await validateLimitMaxRange({
            maxLimit: numericValue,
            userId: userState.id || "",
          });
          setMaxLimitError(!result.valid);
        } catch (error) {
          console.error("Erro na validação:", error);
          setMaxLimitError(false);
        }
      }, 500); // Debounce de 500ms
    } else {
      setMaxLimitError(false);
    }
  };
  // Função de formatação
  const formatNumberWithDots = useCallback((value: string): string => {
    const onlyNumbers = value.replace(/\D/g, "");
    if (!onlyNumbers) return "";

    const number = parseInt(onlyNumbers, 10);
    return isNaN(number) ? "" : number.toLocaleString("pt-BR");
  }, []);

  // Função de validação assíncrona
  const validateMaxValue = useCallback(
    async (value: number) => {
      if (!selectedCompany || selectedCompany.subscription.name !== "POC") {
        return;
      }

      if (value > 0) {
        setIsValidating(true);
        try {
          const result = await validateLimitMaxRange({
            maxLimit: value,
            userId: userState.id || "",
          });
          setMaxLimitError(!result.valid);
        } catch (error) {
          console.error("Erro na validação:", error);
          setMaxLimitError(false); // Ou trate o erro conforme necessário
        } finally {
          setIsValidating(false);
        }
      } else {
        setMaxLimitError(false);
      }
    },
    //[selectedCompany, userState.id],
    [selectedCompany, userState.id, validateLimitMaxRange],

  );

  // Efeito para validar quando max mudar
  useEffect(() => {
    // Limpa timeout anterior
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Só valida se max for definido e maior que 0
    if (max !== undefined && max > 0) {
      // Debounce para evitar muitas requisições enquanto digita
      validationTimeoutRef.current = setTimeout(() => {
        validateMaxValue(max);
      }, 500); // Aguarda 500ms após a última digitação
    } else {
      setMaxLimitError(false);
    }

    // Cleanup
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [max, validateMaxValue]);

  // Efeito para inicializar o displayValue se max tiver valor inicial
  useEffect(() => {
    if (max !== undefined && max !== null && max > 0) {
      const formatted = max.toLocaleString("pt-BR");
      setDisplayValue(formatted);
    }
  }, []); // Apenas na montagem

  const [openRangeForm, setOpenRangeForm] = useState(false);
  const [openSeparatedCredits, setOpenSeparatedCredits] = useState(false);
  const [fixedPrice, setFixedPrice] = useState(false);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const plano = selectedCompany?.subscription.name;
  const exigeData =
    selectedCompany?.company.active !== false &&
    ["POC", "PRÉ PAGO"].includes(plano || "");

  const expAt = selectedCompany?.subscription.expiredAt
    ? new Date(selectedCompany.subscription.expiredAt)
    : null;

  // 1) Está vazio?
  const faltaData = exigeData && !expAt;

  // 2) Está inválido (<= hoje)?
  const dataIncorreta = exigeData && Boolean(expAt && expAt <= hoje);

  // Regras específicas por tipo de plano
  const isFirstRange = selectedCompany?.subscription?.ranges?.length === 0;
  const isSecondRangeOrMore =
    (selectedCompany?.subscription?.ranges?.length ?? 0) >= 1;

  // FEE MENSAL: primeiro range sempre fixo, segundo em diante por registro
  const isFeeMensalFirstRange = plano === "FEE MENSAL" && isFirstRange;
  const isFeeMensalSecondOrMore = plano === "FEE MENSAL" && isSecondRangeOrMore;

  // PÓS PAGO: sempre por registro
  const isPosPago = plano === "PÓS PAGO";

  // PRÉ PAGO: sempre fixo
  const isPrePago = plano === "PRÉ PAGO";

  // POC: sempre 0,00
  const isPoc = plano === "POC";

  // Controla quando permitir infinito
  const allowInfinite = () => {
    if (isPoc) return false; // POC não permite infinito
    if (isPrePago) return false; // PRÉ PAGO não permite infinito
    if (isFeeMensalFirstRange) return false; // FEE MENSAL primeiro range não permite infinito
    return true; // PÓS PAGO e FEE MENSAL segundo range em diante permitem infinito
  };

  // Controla quando permitir switch de preço fixo
  const allowFixedPriceSwitch = () => {
    if (isPosPago) return false; // PÓS PAGO sempre por registro
    if (isPrePago) return false; // PRÉ PAGO sempre fixo
    if (isPoc) return false; // POC sempre 0,00
    if (isFeeMensalFirstRange) return false; // FEE MENSAL primeiro range sempre fixo
    if (isFeeMensalSecondOrMore) return false; // FEE MENSAL segundo range em diante sempre por registro
    return true; // Outros casos permitem escolha
  };

  // logo no topo do seu componente
  const activeSellerOptions = useMemo(
    () =>
      userState.userList
        .filter((u) => u.userType === "SELLER" && u.status === true)
        .map((u) => ({ label: u.name, value: u.id })),
    [userState.userList],
  );
  useEffect(() => {
    const sellerId = selectedCompany?.subscription.sellerId;
    if (sellerId) {
      // sincroniza o seller do Redux com o sellerId da company
      selectSeller({ userId: sellerId });
    } else {
      // limpa o vendedor se não houver sellerId na company
      dispatch(resetSelectedSellerState());
    }
  }, [selectedCompany?.subscription.sellerId, dispatch, selectSeller]);
  return (
    <>
      <Dialog
        fullScreen
        open={open}
        TransitionComponent={Transition}
        onClose={() => {
          closeCompanyForm();
          resetSelectedCompanyResaleConfig();
        }}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              textTransform={"uppercase"}
              sx={{ ml: 2, flex: 1 }}
              variant="h6"
              component="div"
            >
              {selectedCompany?.company.id ? "Editar" : "Cadastrar"} Empresa
            </Typography>
            <Button
              autoFocus
              color="inherit"
              onClick={() => {
                closeCompanyForm();
                resetSelectedCompanyResaleConfig();
              }}
            >
              Fechar
            </Button>
          </Toolbar>
        </AppBar>
        <Box p={3}>
          <form>
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: "bold",
                  }}
                  color="primary"
                  align="center"
                  variant="h6"
                >
                  Dados da Empresa
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider
                  sx={{
                    backgroundColor: "primary.main",
                    height: "2px",
                    width: "50%",
                    margin: "0 auto",
                  }}
                />
              </Grid>
              <Grid container spacing={3} my={2}>
                <Grid item xs={12}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    value={formatCnpj(selectedCompany?.company.cnpj ?? "")}
                    error={!companyFormFields.cnpj.isValid}
                    helperText={
                      !companyFormFields.cnpj.isValid
                        ? companyFormFields.cnpj.error?.message
                        : ""
                    }
                    onChange={(e) => {
                      //const formattedCnpj = onlyNumbers(e.target.value);
                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            cnpj: e.target.value,
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    label="CNPJ"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type="text"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            disabled={userState.userType === "SELLER"}
                            onClick={() => {
                              feedCompanyForm();
                            }}
                          >
                            <SearchIcon />
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!companyFormFields.razaoSocial.isValid}
                    helperText={
                      !companyFormFields.razaoSocial.isValid
                        ? companyFormFields.razaoSocial.error?.message
                        : ""
                    }
                    value={selectedCompany?.company.razaoSocial}
                    onChange={(e) => {
                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            razaoSocial: e.target.value.toUpperCase(),
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    label="Razão Social"
                    fullWidth
                    type="text"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={selectedCompany?.company.nomeFantasia}
                    onChange={(e) => {
                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            nomeFantasia: e.target.value.toUpperCase(),
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    label="Nome Fantasia"
                    fullWidth
                    type="text"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    error={!companyFormFields.email.isValid}
                    helperText={
                      !companyFormFields.email.isValid
                        ? companyFormFields.email.error?.message
                        : ""
                    }
                    value={selectedCompany?.company.email}
                    onChange={(e) => {
                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            email: e.target.value
                              .replace(" ", "")
                              .toLocaleLowerCase(),
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="E-mail"
                    fullWidth
                    type="email"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    error={!companyFormFields.phone.isValid}
                    helperText={
                      !companyFormFields.phone.isValid
                        ? companyFormFields.phone.error?.message
                        : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="Telefone"
                    fullWidth
                    type="phone"
                    value={formatTelefoneBrazil(
                      selectedCompany?.company.phone ?? "",
                    )}
                    onChange={(e) => {
                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            phone: onlyNumbers(e.target.value),
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    error={!companyFormFields.address.postalCode.isValid}
                    helperText={
                      !companyFormFields.address.postalCode.isValid
                        ? companyFormFields.address.postalCode.error?.message
                        : ""
                    }
                    label="CEP"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                    type="text"
                    value={
                      selectedCompany?.company.address.postalCode
                        ? selectedCompany.company.address.postalCode.length ===
                          8
                          ? formatCep(
                              selectedCompany.company.address.postalCode,
                            )
                          : selectedCompany.company.address.postalCode
                        : ""
                    }
                    onChange={(e) => {
                      if (e.target.value.length > 8) return;

                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            address: {
                              ...selectedCompany.company.address,
                              postalCode: onlyNumbers(e.target.value),
                            },
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            disabled={userState.userType === "SELLER"}
                            onClick={() => {
                              feedAddressFields();
                            }}
                          >
                            <SearchIcon />
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    error={!companyFormFields.address.state.isValid}
                    helperText={
                      !companyFormFields.address.state.isValid
                        ? companyFormFields.address.state.error?.message
                        : ""
                    }
                    value={selectedCompany?.company.address.state}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onChange={(e) => {
                      if (e.target.value.length > 2) return;

                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            address: {
                              ...selectedCompany.company.address,
                              state: e.target.value.toUpperCase(),
                            },
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    label="UF"
                    fullWidth
                    type="text"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    error={!companyFormFields.address.city.isValid}
                    helperText={
                      !companyFormFields.address.city.isValid
                        ? companyFormFields.address.city.error?.message
                        : ""
                    }
                    value={selectedCompany?.company.address.city}
                    onChange={(e) => {
                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            address: {
                              ...selectedCompany.company.address,
                              city: e.target.value
                                .replace("  ", " ")
                                .trim()
                                .toUpperCase(),
                            },
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="Cidade"
                    fullWidth
                    type="text"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    error={!companyFormFields.address.street.isValid}
                    helperText={
                      !companyFormFields.address.street.isValid
                        ? companyFormFields.address.street.error?.message
                        : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={selectedCompany?.company.address.street}
                    onChange={(e) => {
                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            address: {
                              ...selectedCompany.company.address,
                              street: e.target.value.replace("  ", " ").trim(),
                            },
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    label="Logradouro"
                    fullWidth
                    type="text"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    error={!companyFormFields.address.neighbourhood.isValid}
                    helperText={
                      !companyFormFields.address.neighbourhood.isValid
                        ? companyFormFields.address.neighbourhood.error?.message
                        : ""
                    }
                    value={selectedCompany?.company.address.neighbourhood}
                    onChange={(e) => {
                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            address: {
                              ...selectedCompany.company.address,
                              neighbourhood: e.target.value
                                .replace("  ", " ")
                                .trim()
                                .toUpperCase(),
                            },
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="Bairro"
                    fullWidth
                    type="text"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    disabled={userState.userType === "SELLER"}
                    error={!companyFormFields.address.houseNumber.isValid}
                    helperText={
                      !companyFormFields.address.houseNumber.isValid
                        ? companyFormFields.address.houseNumber.error?.message
                        : ""
                    }
                    value={selectedCompany?.company.address.houseNumber}
                    onChange={(e) => {
                      if (selectedCompany) {
                        selectCompany({
                          company: {
                            ...selectedCompany.company,
                            address: {
                              ...selectedCompany.company.address,
                              houseNumber: e.target.value,
                            },
                          },
                          subscription: {
                            ...selectedCompany.subscription,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="Número"
                    fullWidth
                    type="text"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    disabled={userState.userType === "SELLER"}
                    options={activeSellerOptions}
                    value={
                      selectedSeller
                        ? {
                            label: selectedSeller.name,
                            value: selectedSeller.id,
                          }
                        : (activeSellerOptions.find(
                            (opt) =>
                              opt.value ===
                              selectedCompany?.subscription.sellerId,
                          ) ?? null)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.value === value?.value
                    }
                    onChange={(_, newValue) => {
                      if (newValue) {
                        selectSeller({ userId: newValue.value });
                      }
                    }}
                    renderInput={(props) => (
                      <TextField
                        {...props}
                        label="Digite ou clique para selecionar um vendedor"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                {company?.master === true ||
                (company?.resale === true &&
                  subscription.subscription?.partnerCols === true) ? (
                  <Grid
                    item
                    xs={company.master === true ? 3 : 6}
                    display={"flex"}
                    justifyContent={"center"}
                  >
                    <FormGroup
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            disabled={userState.userType === "SELLER"}
                            checked={selectedCompany?.subscription.partnerCols}
                            onChange={(e) => {
                              if (selectedCompany) {
                                selectCompany({
                                  company: {
                                    ...selectedCompany.company,
                                  },
                                  subscription: {
                                    ...selectedCompany.subscription,
                                    partnerCols: e.target.checked,
                                  },
                                  separatedCredits:
                                    selectedCompany.separatedCredits,
                                  resaleConfig: resaleConfig,
                                });
                              }
                            }}
                          />
                        }
                        label="Colunas de sócio"
                      />
                    </FormGroup>
                  </Grid>
                ) : null}
                {company?.master === true ? (
                  <Grid item xs={3} display={"flex"} justifyContent={"center"}>
                    <FormGroup
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            disabled={userState.userType === "SELLER"}
                            checked={selectedCompany?.company.resale}
                            onChange={(e) => {
                              if (selectedCompany) {
                                selectCompany({
                                  company: {
                                    ...selectedCompany.company,
                                    resale: e.target.checked,
                                  },
                                  subscription: selectedCompany.subscription,
                                  separatedCredits:
                                    selectedCompany.separatedCredits,
                                  resaleConfig: resaleConfig,
                                });
                              }
                            }}
                          />
                        }
                        label="Revenda"
                      />
                    </FormGroup>
                  </Grid>
                ) : null}
              </Grid>
              {selectedCompany?.company.resale === true ? (
                <WhiteLabelCompanyForm />
              ) : null}
              <Grid item xs={12} mt={1}>
                <Typography
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: "bold",
                  }}
                  color="primary"
                  align="center"
                  variant="h6"
                  component="div"
                >
                  Dados do plano
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider
                  sx={{
                    backgroundColor: "primary.main",
                    height: "2px",
                    width: "50%",
                    margin: "0 auto",
                  }}
                />
              </Grid>
              <Grid container spacing={3} mt={6}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    size="medium"
                    disabled={userState.userType === "SELLER"}
                    error={!companyFormFields.subscription.name.isValid}
                    helperText={
                      !companyFormFields.subscription.name.isValid
                        ? companyFormFields.subscription.name.error?.message
                        : ""
                    }
                    value={selectedCompany?.subscription.name}
                    onChange={(e) => {
                      setMax(undefined);
                      setDisplayValue("");
                      setFixedPrice(false); // Primeiro range = fixo

                      if (selectedCompany) {
                        selectCompany({
                          company: { ...selectedCompany.company },
                          subscription: {
                            ...selectedCompany.subscription,
                            name: e.target.value,
                          },
                          separatedCredits: selectedCompany.separatedCredits,
                          resaleConfig: resaleConfig,
                        });
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    label="Nome do plano"
                    fullWidth
                  >
                    {["FEE MENSAL", "PÓS PAGO", "POC", "PRÉ PAGO"].map(
                      (option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ),
                    )}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  {/* 2º: só para POC mostramos o DatePicker; para outros, nada ou um field disabled */}
                  {["POC", "PRÉ PAGO"].includes(
                    selectedCompany?.subscription.name || "",
                  ) ? (
                    <LocalizationProvider
                      dateAdapter={AdapterDateFns}
                      adapterLocale={ptBR}
                    >
                      <DatePicker
                        label="Data de expiração"
                        disablePast
                        value={
                          selectedCompany?.subscription.expiredAt
                            ? new Date(selectedCompany.subscription.expiredAt)
                            : null
                        }
                        onChange={(date) => {
                          // 1) se vier nulo ou não for Date...
                          if (
                            !(date instanceof Date) ||
                            isNaN(date.getTime())
                          ) {
                            return;
                          }
                          // 2) agora é seguro usar date
                          const hoje = new Date();
                          hoje.setHours(0, 0, 0, 0);
                          date.setHours(0, 0, 0, 0);

                          if (date < hoje) {
                            dispatch(
                              executeAlert({
                                message:
                                  "Data de expiração não pode ser menor que hoje",
                                type: "error",
                              }),
                            );
                            return;
                          }

                          selectCompany({
                            company: { ...selectedCompany!.company },
                            subscription: {
                              ...selectedCompany!.subscription,
                              expiredAt: date.toISOString(),
                            },
                            separatedCredits: selectedCompany!.separatedCredits,
                            resaleConfig,
                          });
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            InputLabelProps: { shrink: true },
                            error: faltaData || dataIncorreta,
                            helperText: faltaData
                              ? "Data de expiração é obrigatória para este plano"
                              : dataIncorreta
                                ? "Data não pode ser menor ou igual a hoje"
                                : "",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  ) : (
                    /* mantém o layout alinhado mesmo quando não for POC */
                    <TextField
                      fullWidth
                      size="medium"
                      disabled
                      label="Data de expiração"
                      InputLabelProps={{ shrink: true }}
                      value=""
                    />
                  )}
                </Grid>
              </Grid>

              <Grid item xs={12} md={12}>
                <Grid
                  container
                  spacing={2}
                  style={{
                    marginTop: "30px",
                  }}
                >
                  <Grid item xs={12}>
                    <Typography
                      sx={{
                        textTransform: "uppercase",
                        fontWeight: "bold",
                      }}
                      color="primary"
                      align="center"
                      variant="h6"
                      component="div"
                    >
                      Ranges de consumo
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      "& .MuiDataGrid-columnHeader": {
                        backgroundColor: theme.palette.primary.main,
                        color:
                          company?.resale === true || company?.resaleId
                            ? theme.palette.primary.contrastText
                            : "#FFF",
                      },
                      "& .MuiDataGrid-cell": {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                      "& .MuiDataGrid-cell:focus": {
                        outline: 0,
                      },
                      minWidth: "100%",
                    }}
                  >
                    <DataGrid
                      showCellVerticalBorder
                      disableColumnFilter
                      disableColumnMenu
                      rowSelection={false}
                      autoHeight={true}
                      localeText={dataGridTexts}
                      pagination={undefined}
                      rows={
                        selectedCompany?.subscription.ranges.map(
                          (range, index) => {
                            return {
                              id: index,
                              min: range.min,
                              max: range.max,
                              fixedPrice: range.fixedPrice,
                              pricePerRegister: range.pricePerRegister,
                              delete:
                                index ===
                                selectedCompany?.subscription.ranges.length - 1,
                            };
                          },
                        ) ?? []
                      }
                      columns={rangeColumns}
                      sx={{
                        "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                          outline: "none !important",
                        },
                        minWidth: "100%",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box my={3}>
                      <Button
                        fullWidth
                        color="primary"
                        onClick={() => {
                          if (
                            selectedCompany?.subscription.name ===
                              "FEE MENSAL" &&
                            (selectedCompany?.subscription.ranges.length ??
                              0) === 0
                          ) {
                            setFixedPrice(true);
                          }
                          // Se for PRÉ PAGO, sempre força fixo também!
                          if (
                            selectedCompany?.subscription.name === "PRÉ PAGO"
                          ) {
                            setFixedPrice(true);
                          }
                          setPrice(undefined); // <-- Zera o preço ao abrir o modal
                          setOpenRangeForm(true);
                        }}
                        disabled={userState.userType === "SELLER"}
                      >
                        Adicionar range
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              {selectedCompany?.company.id !== "" &&
              selectedCompany?.subscription.name !== "POC" ? (
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          textTransform: "uppercase",
                          fontWeight: "bold",
                        }}
                        color="primary"
                        align="center"
                        variant="h6"
                        component="div"
                      >
                        Créditos separados
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        "& .MuiDataGrid-columnHeader": {
                          backgroundColor: theme.palette.primary.main,
                          color:
                            company?.resale === true || company?.resaleId
                              ? theme.palette.primary.contrastText
                              : "#FFF",
                        },
                        "& .MuiDataGrid-cell": {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        },
                        "& .MuiDataGrid-cell:focus": {
                          outline: 0,
                        },
                        minWidth: "100%",
                      }}
                    >
                      <DataGrid
                        showCellVerticalBorder
                        disableColumnFilter
                        disableColumnMenu
                        rowSelection={false}
                        autoHeight={true}
                        localeText={dataGridTexts}
                        pagination={undefined}
                        rows={
                          selectedCompany?.separatedCredits.map(
                            (separatedCredit, index) => {
                              return {
                                id: index,
                                total: separatedCredit.value,
                                price: separatedCredit.price,
                                expDate: separatedCredit.expiresAt,
                                createdAt: separatedCredit.createdAt,
                              };
                            },
                          ) ?? []
                        }
                        columns={separatedCreditsColumns}
                        sx={{
                          "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                            outline: "none !important",
                          },
                          minWidth: "100%",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box my={3}>
                        <Button
                          fullWidth
                          disabled={userState.userType === "SELLER"}
                          color="primary"
                          onClick={() => {
                            setOpenSeparatedCredits(true);
                          }}
                        >
                          Adicionar créditos separados
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              ) : null}
              {selectedCompany?.company.id !== "" ? (
                <Grid item xs={12} mb={2}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          disabled={userState.userType === "SELLER"}
                          checked={selectedCompany?.company.active}
                          onChange={(e) => {
                            if (selectedCompany) {
                              selectCompany({
                                company: {
                                  ...selectedCompany.company,
                                  active: e.target.checked,
                                },
                                subscription: {
                                  ...selectedCompany.subscription,
                                },
                                separatedCredits:
                                  selectedCompany.separatedCredits,
                                resaleConfig: resaleConfig,
                              });
                            }
                          }}
                        />
                      }
                      label={
                        selectedCompany?.company.active ? "Ativa" : "Inativa"
                      }
                    />
                  </FormGroup>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <>
                  <MuiFileInput
                    id="outlined-static"
                    value={file}
                    fullWidth
                    variant="outlined"
                    onChange={(e) => {
                      setCompanyFileImage(e);
                    }}
                    label="Clique aqui para adicionar uma imagem"
                    getInputText={(file) => {
                      return file?.name ?? "Selecione uma imagem";
                    }}
                    InputProps={{
                      inputProps: {
                        accept: ".png, .jpeg, .jpg",
                      },
                    }}
                  />
                  {file !== null ? (
                    selectedCompany?.company?.img ? (
                      <Grid container p={1} justifyContent={"center"}>
                        <Grid
                          item
                          pl={2}
                          pr={2}
                          pt={3}
                          justifyContent={"center"}
                          maxWidth={250}
                        >
                          <img
                            src={selectedCompany.company?.img}
                            alt="Logo da Empresa"
                          />
                        </Grid>
                      </Grid>
                    ) : null
                  ) : null}
                </>
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    my: 3,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    sx={{
                      "&:hover": {
                        backgroundColor: darkenColor(
                          theme.palette.primary.main,
                          10,
                        ),
                      },
                      color: theme.palette.primary.contrastText,
                    }}
                    onClick={() => {
                      if (selectedCompany) {
                        // Cliente inativo pode ser salvo sem plano, ranges ou créditos
                        if (selectedCompany.company.active !== false) {
                          const plano = selectedCompany.subscription.name;
                          const exigeData = ["POC", "PRÉ PAGO"].includes(plano);

                          if (
                            exigeData &&
                            selectedCompany.subscription.expiredAt
                          ) {
                            const hoje = new Date();
                            hoje.setHours(0, 0, 0, 0);

                            const expDate = new Date(
                              selectedCompany.subscription.expiredAt,
                            );
                            expDate.setHours(0, 0, 0, 0);

                            if (expDate <= hoje) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "Data de expiração não pode ser menor nem igual a hoje",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                          }
                          if (
                            exigeData &&
                            !selectedCompany.subscription.expiredAt
                          ) {
                            dispatch(
                              executeAlert({
                                message:
                                  "A data de expiração é obrigatória para este plano",
                                type: "error",
                              }),
                            );
                            return;
                          }

                          // Validação de ranges para todos os planos
                          if (
                            !selectedCompany.subscription.ranges ||
                            selectedCompany.subscription.ranges.length === 0
                          ) {
                            dispatch(
                              executeAlert({
                                message:
                                  "Adicione pelo menos um range de consumo antes de salvar",
                                type: "error",
                              }),
                            );
                            return;
                          }

                          // Validações específicas por tipo de plano
                          const ranges = selectedCompany.subscription.ranges;
                          const lastRange = ranges[ranges.length - 1];

                          if (plano === "FEE MENSAL") {
                            // FEE MENSAL: primeiro range não pode ser infinito, segundo em diante pode
                            if (ranges.length === 1 && !lastRange.max) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "FEE MENSAL: o primeiro range deve ter um valor máximo definido e um preço fixo (não pode ser infinito)",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                          } else if (plano === "PÓS PAGO") {
                            // PÓS PAGO: pode ter infinito no primeiro range
                            // Não precisa de validação específica
                          } else if (plano === "PRÉ PAGO") {
                            // PRÉ PAGO: não pode ter infinito
                            if (!lastRange.max) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "PRÉ PAGO: não permite ranges infinitos. Defina um valor máximo para o último range.",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                          } else if (plano === "POC") {
                            // POC: não pode ter infinito
                            if (!lastRange.max) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "POC: não permite ranges infinitos. Defina um valor máximo para o último range.",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                          }

                          if (plano === "PÓS PAGO") {
                            const hasFixed =
                              selectedCompany.subscription.ranges.some(
                                (r) =>
                                  r.fixedPrice !== undefined &&
                                  r.fixedPrice !== null,
                              );
                            if (hasFixed) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "Plano PÓS PAGO não pode ter ranges com preço fixo. Corrija os ranges antes de salvar.",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                          }

                          if (plano === "FEE MENSAL") {
                            const ranges = selectedCompany.subscription.ranges;
                            // Deve haver pelo menos 2 ranges
                            if (ranges.length < 2) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "No FEE MENSAL, adicione também o range excedente infinito (deixe o campo máximo vazio no último range).",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                            // O último range deve ser infinito (max undefined)
                            const lastRange = ranges[ranges.length - 1];
                            if (
                              lastRange.max !== undefined &&
                              lastRange.max !== null
                            ) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "No FEE MENSAL, o último range deve ser infinito (deixe o campo máximo vazio).",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                            // O último range deve ter pricePerRegister > 0
                            if (
                              lastRange.pricePerRegister === undefined ||
                              lastRange.pricePerRegister === null ||
                              isNaN(lastRange.pricePerRegister) ||
                              lastRange.pricePerRegister <= 0
                            ) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "No FEE MENSAL, o último range deve ter preço por registro maior que zero.",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                            // Todos os ranges intermediários (exceto o último) devem ter max definido
                            for (let i = 0; i < ranges.length - 1; i++) {
                              if (
                                ranges[i].max === undefined ||
                                ranges[i].max === null
                              ) {
                                dispatch(
                                  executeAlert({
                                    message:
                                      "No FEE MENSAL, todos os ranges (exceto o último) devem ter um valor máximo definido.",
                                    type: "error",
                                  }),
                                );
                                return;
                              }
                            }
                          }

                          if (plano === "PRÉ PAGO") {
                            const ranges = selectedCompany.subscription.ranges;
                            const lastRange = ranges[ranges.length - 1];
                            if (!lastRange.max) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "PRÉ PAGO: é obrigatório definir um valor máximo para o último range.",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                            // Validação já existente: preço fixo
                            if (
                              lastRange.fixedPrice === undefined ||
                              lastRange.fixedPrice === null ||
                              lastRange.fixedPrice <= 0
                            ) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "PRÉ PAGO deve ter preço fixo maior que zero.",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                          }

                          if (plano === "POC") {
                            const ranges = selectedCompany.subscription.ranges;
                            const lastRange = ranges[ranges.length - 1];
                            // max obrigatório
                            if (!lastRange.max) {
                              dispatch(
                                executeAlert({
                                  message:
                                    "POC: não permite ranges infinitos. Defina um valor máximo para o último range.",
                                  type: "error",
                                }),
                              );
                              return;
                            }
                            // // Limite máximo de 10 consultas para POC
                            // if (lastRange.max > 10) {
                            //   dispatch(
                            //     executeAlert({
                            //       message:
                            //         "POC: o máximo de consultas permitido é 10.",
                            //       type: "error",
                            //     }),
                            //   );
                            //   return;
                            // }
                            // preço deve ser 0
                            for (let i = 0; i < ranges.length; i++) {
                              const range = ranges[i];
                              if (
                                (range.fixedPrice !== undefined &&
                                  range.fixedPrice !== 0) ||
                                (range.pricePerRegister !== undefined &&
                                  range.pricePerRegister !== 0)
                              ) {
                                dispatch(
                                  executeAlert({
                                    message:
                                      "POC: todos os ranges devem ter preço igual a R$ 0,00.",
                                    type: "error",
                                  }),
                                );
                                return;
                              }
                            }
                          }
                        }
                        if (
                          resaleConfig?.emailProviderSettings.authType ===
                          "oauth2"
                        ) {
                          const oauth2 =
                            resaleConfig.emailProviderSettings.oauth2;
                          if (!oauth2?.clientId || !oauth2?.clientSecret) {
                            dispatch(
                              executeAlert({
                                message:
                                  'Para OAuth 2.0, os campos Client ID e Client Secret são obrigatórios. Use o botão "Autorizar com Microsoft" para obter os tokens automaticamente.',
                                type: "error",
                              }),
                            );
                            return;
                          }
                        }

                        if (selectedCompany.company.id === "") {
                          createCompany();
                        } else {
                          updateCompany();
                        }
                      }
                    }}
                    disabled={isReadOnly || faltaData || dataIncorreta}
                  >
                    Salvar
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={openRangeForm}
        onClose={() => {
          setOpenRangeForm(false);
        }}
      >
        <DialogTitle
          color="primary"
          fontWeight={"bold"}
          textTransform={"uppercase"}
        >
          Adicionar novo range
        </DialogTitle>
        <DialogContent>
          <DialogContentText py={0.5}>
            O range funciona como uma tabela variável de consumo.
          </DialogContentText>
          <DialogContentText py={0.5}>
            Em "Mínimo", você determina a quantidade de créditos que{" "}
            <strong>iniciará</strong> este range.
          </DialogContentText>
          <DialogContentText py={0.5}>
            Em "Máximo", você determina a quantidade <strong>máxima</strong> de
            créditos que irá compor este range.
          </DialogContentText>
          <DialogContentText py={0.5}>
            Na opção "Preço por registro / Preço fixo", você insere o custo
            definido para este cliente. Caso esteja selecionada a opção "Preço
            por registro", você deve inserir o custo por crédito, ou custo por
            registro cobrado para este cliente. Caso a opção selecionada seja
            "Preço fixo", você insere apenas o custo total determinado com seu
            cliente para este range.
          </DialogContentText>
          <Grid
            container
            spacing={2}
            style={{
              marginTop: "30px",
              padding: "30px",
            }}
          >
            <Grid item xs={6}>
              <TextField
                disabled={
                  (selectedCompany?.subscription.ranges[
                    selectedCompany?.subscription.ranges.length - 1
                  ]?.max
                    ? true
                    : false) ||
                  (selectedCompany?.subscription.ranges.length === 0
                    ? true
                    : false)
                }
                fullWidth
                label="Mínimo"
                type="number"
                value={min}
                onChange={(e) => {
                  setMin(Number(e.target.value));
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                sx={{
                  minHeight: "80px",
                  "& .MuiFormHelperText-root": {
                    marginTop: "4px",
                  },
                  "& input": {
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "0.5px",
                  },
                }}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                fullWidth
                label="Máximo"
                type="text"
                value={displayValue}
                onChange={(e) => {
                  // Pega o valor atual do input
                  const inputValue = e.target.value;

                  // Formata o valor
                  const formattedValue = formatNumberWithDots(inputValue);

                  // Atualiza o display
                  setDisplayValue(formattedValue);

                  // Remove formatação para obter o valor numérico
                  const numericString = formattedValue.replace(/\./g, "");
                  const numericValue = numericString
                    ? Number(numericString)
                    : undefined;

                  // Atualiza o estado numérico para o backend
                  // Isso vai disparar o useEffect que faz a validação
                  setMax(numericValue);

                  // Limpa erro imediatamente ao começar a digitar
                  // (a validação assíncrona vai atualizar se necessário)
                  setMaxLimitError(false);
                }}
                onBlur={() => {
                  // Validação imediata ao sair do campo
                  if (max && max > 0) {
                    validateMaxValue(max);
                  }
                }}
                error={maxLimitError}
                disabled={isValidating}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                InputProps={{
                  startAdornment: (
                    <Box mr={1}>
                      <Typography>R$</Typography>
                    </Box>
                  ),
                }}
                fullWidth
                label={
                  isPoc
                    ? "Preço (sempre R$ 0,00)"
                    : isPrePago
                      ? "Preço fixo (PRÉ PAGO)"
                      : isPosPago
                        ? "Preço por registro (PÓS PAGO)"
                        : isFeeMensalFirstRange
                          ? "Preço fixo (FEE MENSAL)"
                          : isFeeMensalSecondOrMore
                            ? "Preço por registro (excedente)"
                            : fixedPrice
                              ? "Preço fixo"
                              : "Preço por registro"
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={
                  selectedCompany?.subscription.name === "POC"
                    ? "0.00"
                    : formatarValor(price)
                }
                onChange={handleChange}
                disabled={selectedCompany?.subscription.name === "POC"}
              />
            </Grid>
            {allowFixedPriceSwitch() ? (
              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fixedPrice}
                        onChange={(e) => {
                          setFixedPrice(e.target.checked);
                        }}
                      />
                    }
                    label={fixedPrice ? "Preço fixo" : "Preço por registro"}
                  />
                </FormGroup>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  {isPosPago && "PÓS PAGO: sempre preço por registro"}
                  {isPrePago && "PRÉ PAGO: sempre preço fixo"}
                  {isPoc && "POC: sempre R$ 0,00"}
                  {isFeeMensalFirstRange &&
                    "FEE MENSAL: primeiro range sempre preço fixo"}
                  {isFeeMensalSecondOrMore &&
                    "FEE MENSAL: segundo range em diante sempre por registro"}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenRangeForm(false);
              setDisplayValue("");
              setMax(undefined);
            }}
          >
            Cancelar
          </Button>
          <Button
            disabled={
              min === undefined ||
              min === null ||
              (displayValue === "" && plano === "POC" ) ||
              (displayValue !== "" && plano === "FEE MENSAL" && isFeeMensalSecondOrMore === false && isFeeMensalFirstRange === false) ||
              max === null ||
              maxLimitError
            }
            onClick={() => {
              if (isPoc) {
                // POC: preço sempre 0,00
                if (price !== 0 && price !== undefined && price !== null) {
                  dispatch(
                    executeAlert({
                      message: "POC deve ter preço R$ 0,00",
                      type: "error",
                    }),
                  );
                  return;
                }
              } else if (isPrePago) {
                if (!fixedPrice) {
                  dispatch(
                    executeAlert({
                      message: "PRÉ PAGO deve ter preço fixo",
                      type: "error",
                    }),
                  );
                  return;
                }
                if (price === undefined || price === null || price === 0) {
                  dispatch(
                    executeAlert({
                      message: "O preço deve ser maior que 0",
                      type: "error",
                    }),
                  );
                  return;
                }
              } else if (isPosPago) {
                if (fixedPrice) {
                  dispatch(
                    executeAlert({
                      message: "PÓS PAGO deve ter preço por registro",
                      type: "error",
                    }),
                  );
                  return;
                }
                if (price === undefined || price === null || price === 0) {
                  dispatch(
                    executeAlert({
                      message: "O preço deve ser maior que 0",
                      type: "error",
                    }),
                  );
                  return;
                }
              } else if (isFeeMensalFirstRange) {
                if (!fixedPrice) {
                  dispatch(
                    executeAlert({
                      message:
                        "No FEE MENSAL, o primeiro range deve ser preço fixo.",
                      type: "error",
                    }),
                  );
                  return;
                }
                if (price === undefined || price === null || price === 0) {
                  dispatch(
                    executeAlert({
                      message: "O preço deve ser maior que 0",
                      type: "error",
                    }),
                  );
                  return;
                }
              } else if (isFeeMensalSecondOrMore) {
                if (fixedPrice) {
                  dispatch(
                    executeAlert({
                      message:
                        "FEE MENSAL segundo range em diante deve ter preço por registro",
                      type: "error",
                    }),
                  );
                  return;
                }
                if (
                  price === undefined ||
                  price === null ||
                  isNaN(price) ||
                  price === 0
                ) {
                  dispatch(
                    executeAlert({
                      message: "O preço deve ser maior que 0",
                      type: "error",
                    }),
                  );
                  return;
                }
              } else {
                // Outros casos
                if (price === undefined || price === null || price === 0) {
                  dispatch(
                    executeAlert({
                      message: "O preço deve ser maior que 0",
                      type: "error",
                    }),
                  );
                  return;
                }
              }
              if (!allowInfinite() && !max) {
                dispatch(
                  executeAlert({
                    message: "Defina um valor máximo no primeiro range.",
                    type: "error",
                  }),
                );
                return;
              }

              if ((selectedCompany?.subscription.ranges.length ?? 0) > 0) {
                let lastRange = {
                  ...selectedCompany?.subscription.ranges[
                    selectedCompany?.subscription.ranges.length - 1
                  ],
                };
                if (!lastRange?.max) {
                  lastRange.max = min - 1;
                }

                if (selectedCompany) {
                  if (lastRange) {
                    if (min <= (lastRange.min ?? 0)) {
                      dispatch(
                        executeAlert({
                          message:
                            "O valor mínimo deve ser maior que o valor mínimo do último range",
                          type: "error",
                        }),
                      );
                      return;
                    }

                    if (max && max <= min) {
                      dispatch(
                        executeAlert({
                          message:
                            "O valor máximo deve ser maior que o valor mínimo",
                          type: "error",
                        }),
                      );
                      return;
                    }

                    if (max && max <= lastRange.max) {
                      dispatch(
                        executeAlert({
                          message:
                            "O valor máximo deve ser maior que o valor máximo do último range",
                          type: "error",
                        }),
                      );
                      return;
                    }

                    selectCompany({
                      company: {
                        ...selectedCompany.company,
                      },
                      subscription: {
                        ...selectedCompany.subscription,
                        ranges: [
                          ...selectedCompany.subscription.ranges.slice(
                            0,
                            selectedCompany.subscription.ranges.length - 1,
                          ),
                          {
                            max: lastRange.max,
                            min: lastRange.min ?? 0,
                            pricePerRegister: lastRange.pricePerRegister,
                            fixedPrice: lastRange.fixedPrice,
                          },
                          {
                            max: max,
                            min: min,
                            pricePerRegister:
                              selectedCompany.subscription.name === "POC"
                                ? 0
                                : fixedPrice
                                  ? undefined
                                  : price,
                            fixedPrice:
                              selectedCompany.subscription.name === "POC"
                                ? 0
                                : fixedPrice
                                  ? price
                                  : undefined,
                          },
                        ],
                      },
                      separatedCredits: selectedCompany?.separatedCredits,
                      resaleConfig: resaleConfig,
                    });
                  }
                }
              } else {
                if (selectedCompany) {
                  selectCompany({
                    company: {
                      ...selectedCompany.company,
                    },
                    subscription: {
                      ...selectedCompany.subscription,
                      ranges: [
                        {
                          min: min,
                          max: max,
                          pricePerRegister:
                            selectedCompany.subscription.name === "POC"
                              ? 0
                              : fixedPrice
                                ? undefined
                                : price,
                          fixedPrice:
                            selectedCompany.subscription.name === "POC"
                              ? 0
                              : fixedPrice
                                ? price
                                : undefined,
                        },
                      ],
                    },
                    separatedCredits: selectedCompany?.separatedCredits,
                    resaleConfig: resaleConfig,
                  });
                  setMin(0);
                  setMax(undefined);
                  setDisplayValue("");
                  setPrice(undefined);
                }
              }

              setOpenRangeForm(false);
            }}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="lg"
        open={openSeparatedCredits}
        onClose={() => {
          setOpenSeparatedCredits(false);
        }}
      >
        <DialogTitle
          color="primary"
          fontWeight={"bold"}
          textTransform={"uppercase"}
        >
          Adicionar créditos separados{" "}
        </DialogTitle>
        <DialogContent>
          <DialogContentText py={0.5}>
            Os créditos separados funcionam como uma espécie de "pacote".
          </DialogContentText>
          <DialogContentText py={0.5}>
            Em "Total", você determina a quantidade total de créditos que serão
            inseridos para esta empresa. Este será o <strong>limite</strong> de
            registros.
          </DialogContentText>
          <DialogContentText py={0.5}>
            Em "Preço", voce deve inserir o custo total definido para este
            pacote de créditos.
          </DialogContentText>
          <DialogContentText py={0.5}>
            Por fim, em "Data de expiração", você deve inserir a data limite em
            que os usuários desta empresa poderão utilizar os créditos em
            questão. Você pode digitar uma data no formato DD/MM/AAAA, ou clicar
            no ícone de calendário e selecionar manualmente.
          </DialogContentText>
          <Grid
            container
            justifyContent={"center"}
            spacing={2}
            style={{
              marginTop: "30px",
              padding: "30px",
            }}
          >
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Total"
                type="text"
                value={separatedCreditsValue ?? ""}
                onChange={(e) => {
                  const inputValor = e.target.value;
                  if (!/^[0-9]*\.?[0-9]*$/.test(inputValor)) {
                    if (inputValor.includes(",")) {
                      // Alerta específico para vírgulas
                      dispatch(
                        executeAlert({
                          message:
                            "Use ponto (.) como separador decimal, vírgula não é permitida.",
                          type: "error",
                        }),
                      );
                    }
                    return;
                  }

                  setSeparatedCreditsValue(Number(inputValor));
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                InputProps={{
                  startAdornment: (
                    <Box mr={1}>
                      <Typography>R$</Typography>
                    </Box>
                  ),
                }}
                fullWidth
                label={"Preço"}
                InputLabelProps={{
                  shrink: true,
                }}
                value={"0,00"}
                disabled
              />
            </Grid>
            <Grid item>
              <LocalizationProvider
                dateFormats={{ fullDate: "MM/yyyy" }}
                dateAdapter={AdapterDateFns}
                adapterLocale={ptBR}
              >
                <DatePicker
                  disablePast
                  value={expDate ? new Date(expDate) : null}
                  onChange={(date) => {
                    if (date) {
                      setExpDate(date);
                    } else {
                      setExpDate(null);
                    }
                  }}
                  label="Data de expiração"
                  slotProps={{
                    day: {
                      sx: {
                        "&.MuiPickersDay-root.Mui-selected": {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          "&:hover": {
                            backgroundColor: darkenColor(
                              theme.palette.primary.main,
                              10,
                            ),
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenSeparatedCredits(false);
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              addSeparatedCredits({
                separatedCredits: {
                  value: separatedCreditsValue ?? 0,
                  price: separatedCreditsPrice ?? 0,
                  expiresAt: expDate,
                },
              });
              setOpenSeparatedCredits(false);
            }}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
